// Standalone embeddable chat widget. Built separately (vite.widget.config.ts) into
// dist/widget/chat.js with zero dependencies on the admin app — it runs on arbitrary
// third-party websites, so it must not import React or src/api/client.ts.
import type { ChatWidgetConfig } from '@/api/chatWidget'

interface MultiChatGlobal {
  token: string
  apiUrl: string
}

interface WsMessage {
  Type: string
  id?: string
  chat_id?: string
  sender_id?: string
  recipient_id?: string
  content?: string
  options?: string[]
  direction?: string
}

declare global {
  interface Window {
    MultiChat?: MultiChatGlobal
  }
}

const ICONS: Record<string, string> = {
  chat: '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>',
  message: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  headphones: '<path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>',
  zap: '<path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>',
  send: '<path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/>',
}

function svgIcon(name: string, size: number, color: string): string {
  const path = ICONS[name] ?? ICONS.chat
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${path}</svg>`
}

function wsHostFromApiUrl(apiUrl: string): string {
  const u = new URL(apiUrl)
  const proto = u.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${u.host}`
}

function randomId(): string {
  return Math.random().toString(36).slice(2, 10)
}

// Single check (sent), double check (delivered/seen — distinguished by the
// mc-tick-seen CSS class's opacity, not a different icon, same as a
// WhatsApp-style read receipt).
const TICK_SENT = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>'
const TICK_DOUBLE = '<svg width="14" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 7 17l-5-5"/><path d="M22 10 11 21"/></svg>'

function init() {
  const cfg = window.MultiChat
  if (!cfg?.token || !cfg?.apiUrl) {
    console.error('[MultiChat] window.MultiChat = { token, apiUrl } is required')
    return
  }

  const uidKey = `mc_chat_uid_${cfg.token}`
  const chatIdKey = `mc_chat_id_${cfg.token}`
  let guestId = localStorage.getItem(uidKey) ?? ''
  if (!guestId) {
    guestId = `guest-${randomId()}`
    localStorage.setItem(uidKey, guestId)
  }
  let chatId = localStorage.getItem(chatIdKey) ?? ''

  fetch(`${cfg.apiUrl}/api/v1/public/chat-widget/config?token=${encodeURIComponent(cfg.token)}`)
    .then(res => {
      if (!res.ok) throw new Error(`config fetch failed: ${res.status}`)
      return res.json()
    })
    .then((data: { orgId: string; settings: ChatWidgetConfig; isOpenNow?: boolean }) => {
      if (!data?.orgId || !data?.settings?.enabled) return
      render(cfg, data.orgId, data.settings, guestId, chatId, chatIdKey, data.isOpenNow ?? true)
    })
    .catch(err => console.error('[MultiChat] failed to load widget config', err))
}

function render(
  cfg: MultiChatGlobal,
  orgId: string,
  settings: ChatWidgetConfig,
  guestId: string,
  initialChatId: string,
  chatIdKey: string,
  isOpenNow: boolean,
) {
  const b = settings.branding
  const isRight = b.position !== 'bottom-left'
  const bubblePx = b.launcherSize === 'sm' ? 48 : b.launcherSize === 'lg' ? 68 : 58

  const style = document.createElement('style')
  style.textContent = `
    #mc-widget { position: fixed; bottom: 20px; ${isRight ? 'right' : 'left'}: 20px; z-index: 999999; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
    #mc-bubble { width: ${bubblePx}px; height: ${bubblePx}px; border-radius: 50%; background: ${b.primaryColor}; border: none; cursor: pointer; box-shadow: 0 6px 16px rgba(0,0,0,0.2); display: flex; align-items: center; justify-content: center; transition: transform 0.15s ease; }
    #mc-bubble:hover { transform: scale(1.06); }
    #mc-window { display: none; flex-direction: column; position: fixed; bottom: ${bubblePx + 32}px; ${isRight ? 'right' : 'left'}: 20px; width: 360px; height: 520px; max-height: 70vh; background: #fff; border-radius: 16px; box-shadow: 0 12px 32px rgba(0,0,0,0.22); overflow: hidden; }
    #mc-window.mc-open { display: flex; }
    #mc-header { background: ${b.primaryColor}; color: ${b.textColor}; padding: 14px 16px; display: flex; align-items: center; gap: 10px; }
    #mc-header img { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; }
    #mc-header .mc-title { font-weight: 600; font-size: 15px; flex: 1; }
    #mc-header button { background: transparent; border: none; color: ${b.textColor}; opacity: 0.8; cursor: pointer; font-size: 18px; line-height: 1; }
    #mc-messages { flex: 1; overflow-y: auto; padding: 16px; background: #f9fafb; display: flex; flex-direction: column; gap: 10px; font-size: 14px; }
    .mc-msg { max-width: 78%; padding: 9px 13px; border-radius: 16px; line-height: 1.45; word-wrap: break-word; }
    .mc-msg.me { align-self: flex-end; background: ${b.primaryColor}; color: ${b.textColor}; border-bottom-right-radius: 4px; display: flex; align-items: flex-end; gap: 5px; }
    .mc-msg.them { align-self: flex-start; background: #fff; color: #111827; box-shadow: 0 1px 2px rgba(0,0,0,0.08); border-bottom-left-radius: 4px; }
    .mc-msg.system { align-self: center; background: transparent; color: #6b7280; font-size: 12px; padding: 2px 0; }
    .mc-tick { flex-shrink: 0; opacity: 0.6; }
    .mc-tick-seen { opacity: 1; }
    .mc-options { display: flex; flex-direction: column; gap: 6px; margin-top: 8px; align-self: stretch; }
    .mc-options button { padding: 8px 12px; border-radius: 8px; border: none; background: ${b.primaryColor}; color: ${b.textColor}; font-size: 13px; text-align: left; cursor: pointer; }
    #mc-input-row { display: flex; align-items: flex-end; gap: 8px; padding: 10px; border-top: 1px solid #e5e7eb; background: #fff; }
    #mc-input-row textarea { flex: 1; resize: none; border: 1px solid #d1d5db; border-radius: 18px; padding: 9px 14px; font-size: 14px; max-height: 80px; outline: none; font-family: inherit; }
    #mc-input-row textarea:focus { border-color: ${b.primaryColor}; }
    #mc-send { width: 38px; height: 38px; border-radius: 50%; border: none; background: ${b.primaryColor}; color: ${b.textColor}; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
    #mc-footer { text-align: center; font-size: 11px; color: #9ca3af; padding: 6px 0; background: #fff; }
  `
  document.head.appendChild(style)

  const root = document.createElement('div')
  root.id = 'mc-widget'
  root.innerHTML = `
    <button id="mc-bubble" aria-label="Open chat">${svgIcon(b.bubbleIcon, bubblePx * 0.45, b.textColor)}</button>
    <div id="mc-window">
      <div id="mc-header">
        ${b.companyLogoUrl ? `<img src="${b.companyLogoUrl}" alt="">` : ''}
        <span class="mc-title">${escapeHtml(b.companyName || 'Support')}</span>
        <button id="mc-close" aria-label="Close chat">&times;</button>
      </div>
      <div id="mc-messages"></div>
      <div id="mc-input-row">
        <textarea id="mc-text" rows="1" placeholder="${escapeHtml(b.inputPlaceholder)}"></textarea>
        <button id="mc-send" aria-label="Send">${svgIcon('send', 16, b.textColor)}</button>
      </div>
      ${b.showBranding ? '<div id="mc-footer">Powered by Multiaccess</div>' : ''}
    </div>
  `
  document.body.appendChild(root)

  const bubble = root.querySelector<HTMLButtonElement>('#mc-bubble')!
  const win = root.querySelector<HTMLDivElement>('#mc-window')!
  const closeBtn = root.querySelector<HTMLButtonElement>('#mc-close')!
  const messages = root.querySelector<HTMLDivElement>('#mc-messages')!
  const textarea = root.querySelector<HTMLTextAreaElement>('#mc-text')!
  const sendBtn = root.querySelector<HTMLButtonElement>('#mc-send')!

  let chatId = initialChatId
  let socket: WebSocket | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let opened = false

  function appendMessage(kind: 'me' | 'them' | 'system', text: string): HTMLDivElement {
    const el = document.createElement('div')
    el.className = `mc-msg ${kind}`
    if (kind === 'me') {
      // Tagged with a temp local id so the echo (which carries the real,
      // server-assigned id) can be matched up, and later delivered/seen
      // events can find this exact bubble to update its tick.
      el.dataset.msgId = `local-${Date.now()}-${randomId()}`
      const textEl = document.createElement('span')
      textEl.textContent = text
      el.appendChild(textEl)
      const tick = document.createElement('span')
      tick.className = 'mc-tick'
      tick.innerHTML = TICK_SENT
      el.appendChild(tick)
    } else {
      el.textContent = text
    }
    messages.appendChild(el)
    messages.scrollTop = messages.scrollHeight
    return el
  }

  function updateTick(el: Element, status: 'delivered' | 'seen') {
    const tick = el.querySelector<HTMLSpanElement>('.mc-tick')
    if (!tick) return
    if (status === 'seen') {
      tick.className = 'mc-tick mc-tick-seen'
      tick.innerHTML = TICK_DOUBLE
    } else if (!tick.className.includes('mc-tick-seen')) {
      tick.className = 'mc-tick'
      tick.innerHTML = TICK_DOUBLE
    }
  }

  function sendSeen() {
    if (!chatId || socket?.readyState !== WebSocket.OPEN) return
    socket.send(JSON.stringify({ Type: 'seen', chat_id: chatId }))
  }

  function appendPrechat(question: string, options: string[]) {
    const wrap = document.createElement('div')
    wrap.className = 'mc-msg them'
    const q = document.createElement('div')
    q.textContent = question
    wrap.appendChild(q)
    const optWrap = document.createElement('div')
    optWrap.className = 'mc-options'
    options.forEach(opt => {
      const btn = document.createElement('button')
      btn.textContent = opt
      btn.onclick = () => {
        appendMessage('me', opt)
        send(opt)
        optWrap.remove()
      }
      optWrap.appendChild(btn)
    })
    wrap.appendChild(optWrap)
    messages.appendChild(wrap)
    messages.scrollTop = messages.scrollHeight
  }

  function send(content: string) {
    if (!content.trim() || socket?.readyState !== WebSocket.OPEN) return
    const msg: WsMessage = {
      Type: 'text',
      recipient_id: 'support',
      content,
      ...(chatId ? { chat_id: chatId } : {}),
    }
    socket.send(JSON.stringify(msg))
  }

  function connect() {
    // The server derives and authorizes orgId from this token (and the page's
    // origin) rather than trusting X-Org-ID — it's kept here only for log readability.
    // referrer/page are sent explicitly from document.referrer/location.href
    // rather than relying on the server reading this request's Referer header —
    // that header reflects the widget's *current* embed page, not the true
    // external site (e.g. a Google search) that brought the visitor here.
    const params = new URLSearchParams({
      'X-Org-ID': orgId,
      userType: 'customer',
      userID: guestId,
      token: cfg.token,
      referrer: document.referrer,
      page: window.location.href,
    })
    const wsUrl = `${wsHostFromApiUrl(cfg.apiUrl)}/api/v1/multichannel/ws?${params.toString()}`
    socket = new WebSocket(wsUrl)

    socket.onmessage = event => {
      let msg: WsMessage
      try {
        msg = JSON.parse(event.data)
      } catch {
        return
      }

      if (msg.chat_id && msg.chat_id !== chatId) {
        chatId = msg.chat_id
        localStorage.setItem(chatIdKey, chatId)
      }

      switch (msg.Type) {
        case 'system':
          if (msg.content) appendMessage('system', msg.content)
          break
        case 'prechat':
          if (msg.content) appendPrechat(msg.content, msg.options ?? [])
          break
        case 'assignment':
          break
        case 'delivered':
          if (msg.id) {
            const el = messages.querySelector(`[data-msg-id="${msg.id}"]`)
            if (el) updateTick(el, 'delivered')
          }
          break
        case 'seen':
          messages.querySelectorAll('.mc-msg.me').forEach(el => updateTick(el, 'seen'))
          break
        case 'resolved':
          appendMessage('system', 'This conversation has been marked as resolved.')
          textarea.disabled = true
          textarea.placeholder = 'This conversation is resolved'
          sendBtn.disabled = true
          break
        case 'text':
          if (msg.direction === 'outgoing') {
            // Echo of our own message with its real, server-assigned id —
            // adopt it onto the oldest still-unconfirmed local bubble so
            // later delivered/seen events (keyed by that real id) find it.
            if (msg.id) {
              const pending = messages.querySelector('[data-msg-id^="local-"]')
              if (pending) (pending as HTMLElement).dataset.msgId = msg.id
            }
            break
          }
          if (msg.content) appendMessage('them', msg.content)
          sendSeen()
          break
        default:
          break
      }
    }

    socket.onclose = () => {
      reconnectTimer = setTimeout(connect, 3000)
    }
    socket.onerror = () => {
      socket?.close()
    }
  }

  function openWindow() {
    if (!opened) {
      opened = true
      if (b.greeting) appendMessage('them', b.greeting)
      if (!isOpenNow && b.awayMessage) appendMessage('them', b.awayMessage)
      connect()
    } else {
      sendSeen()
    }
    win.classList.add('mc-open')
  }

  bubble.addEventListener('click', () => {
    if (win.classList.contains('mc-open')) {
      win.classList.remove('mc-open')
    } else {
      openWindow()
    }
  })
  closeBtn.addEventListener('click', () => win.classList.remove('mc-open'))

  function submit() {
    const text = textarea.value.trim()
    if (!text) return
    appendMessage('me', text)
    send(text)
    textarea.value = ''
  }
  sendBtn.addEventListener('click', submit)
  textarea.addEventListener('keydown', e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  })

  window.addEventListener('beforeunload', () => {
    if (reconnectTimer) clearTimeout(reconnectTimer)
    socket?.close()
  })
}

function escapeHtml(s: string): string {
  const div = document.createElement('div')
  div.textContent = s
  return div.innerHTML
}

init()
